import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ProductsService } from 'app/core/services/admin/products.service';
import { FarmsService } from 'app/core/services/admin/farms.service';
import { ProductStockService } from 'app/core/services/admin/product-stock.service';
import { Product } from 'app/core/models/admin/products.model';
import { Farm } from 'app/core/models/admin/farms.model';
import { AlertService } from 'app/core/alert/alert.service';

@Component({
  selector: 'app-stock-form',
  templateUrl: './stock-form.component.html',
  styleUrls: ['./stock-form.component.scss'],
})
export class StockFormComponent implements OnInit {
  stockForm: FormGroup;
  products: Product[] = [];
  farms: Farm[] = [];
  formTitle: string = 'Adicionar Estoque';
  removeBatchDataBase: boolean = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<StockFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any, // Se houver dados para edição
    private productsService: ProductsService,
    private farmsService: FarmsService,
    private _alertService: AlertService,
    private productStockService: ProductStockService
  ) {}

  ngOnInit(): void {
    // Inicializa o formulário já com um produto (estoque) e um lote padrão
    this.initializeForm();

    // Carrega os selects de produtos e fazendas
    /*this.productsService.getProducts().subscribe(prodList => {
      this.products = prodList;
    });*/
    this.productsService.getProducts().subscribe(prodList => {
      this.products = prodList.sort((a, b) =>
        a.description.localeCompare(b.description)  
      );
    });
    this.farmsService.getFarms().subscribe(farmsList => {
      this.farms = farmsList;
    });

    // Se houver dados para edição
    if (this.data && this.data.stockData) {
      // Define o título para atualização
      this.formTitle = 'Atualizar Estoque';
      const stockData = this.data.stockData;
      // Se os dados não vierem estruturados em "inventory", cria a estrutura esperada
      if (!stockData.inventory) {
        stockData.inventory = [
          {
            product: stockData.product_id,
            minimun_level: stockData.minimun_level,
            batches: stockData.batch || [],
          },
        ];
      }
      // Preenche o campo de fazenda
      this.stockForm.patchValue({ farm_id: stockData.farm_id });
      // Atualiza o FormArray "inventory"
      const inventoryArray = this.stockForm.get('inventory') as FormArray;
      inventoryArray.clear();
      stockData.inventory.forEach(prod => {
        const prodGroup = this.createStockEntry();
        prodGroup.patchValue(prod);
        // Atualiza o FormArray de lotes ("batches") para cada produto
        const batchesArray = prodGroup.get('batches') as FormArray;
        batchesArray.clear();
        prod.batches.forEach(batch => {
          /*if (batch.validity) {
            const [year, month, day] = batch.validity.split('-').map(Number);
            batch.validity = new Date(year, month - 1, day);
          }*/

          if (batch.validity && typeof batch.validity === 'string') {
            const [year, month, day] = batch.validity.split('-').map(Number);
            batch.validity = new Date(year, month - 1, day);
          } else if (batch.validity instanceof Date) {
          } else {
            console.warn('Formato inválido para batch.validity:', batch.validity);
          }

          const batchGroup = this.createBatchEntry();
          batchGroup.patchValue(batch);
          batchesArray.push(batchGroup);          
        });

        inventoryArray.push(prodGroup);
      });
      // Desabilita os campos que não devem ser editados
      this.stockForm.get('farm_id').disable();
      inventoryArray.controls.forEach((prodGroup: FormGroup) => {
        prodGroup.get('product').disable();
        prodGroup.get('minimun_level').disable();
        const batchesArray = prodGroup.get('batches') as FormArray;
        batchesArray.controls.forEach((batchGroup: FormGroup) => {
          batchGroup.get('batch').disable();
          batchGroup.get('validity').disable();
          batchGroup.get('mode').disable();
          // quantity e operation_type permanecem habilitados
        });
      });
    }
  }

  initializeForm(): void {
    this.stockForm = this.fb.group({
      farm_id: [null, Validators.required],
      // Inicialmente já cria um produto com um lote padrão
      stock_entry_blocked: [false],
      inventory: this.fb.array([this.createStockEntry()]),
    });
  }

  // Getter para o FormArray de produtos
  get inventoryFormArray(): FormArray {
    return this.stockForm.get('inventory') as FormArray;
  }

  // Cria um novo FormGroup para um produto (estoque) já com um lote padrão
  createStockEntry(): FormGroup {
    return this.fb.group({
      product: [null, Validators.required],
      minimun_level: [null],
      batches: this.fb.array([this.createBatchEntry()]),
    });
  }

  // Cria um novo FormGroup para um lote
  createBatchEntry(): FormGroup {
    return this.fb.group({
      id: [null],  // <- esse campo é necessário para deletar do backend
      batch: ['', Validators.required],
      validity: ['', Validators.required],
      quantity: [null, [Validators.required, Validators.min(0.001)]],
      mode: ['OnDemand', Validators.required],
      operation_type: ['verification', Validators.required],
    });
  }

  // Adiciona um novo produto (estoque)
  addStock(): void {
    this.inventoryFormArray.push(this.createStockEntry());
  }

  // Remove um produto
  removeStock(index: number): void {
    this.inventoryFormArray.removeAt(index);
  }

  // Retorna o FormArray de lotes para um produto específico
  getBatches(stock: FormGroup): FormArray {
    return stock.get('batches') as FormArray;
  }

  // Adiciona um novo lote para um produto específico
  addBatch(stockIndex: number): void {
    const stockGroup = this.inventoryFormArray.at(stockIndex) as FormGroup;
    const batches = stockGroup.get('batches') as FormArray;
    batches.push(this.createBatchEntry());
  }

  // Remove um lote de um produto
  /*removeBatch(stockIndex: number, batchIndex: number): void {
    const stockGroup = this.inventoryFormArray.at(stockIndex) as FormGroup;
    const batches = stockGroup.get('batches') as FormArray;
    batches.removeAt(batchIndex);
  }*/

  removeBatch(stockIndex: number, batchIndex: number): void {
    const stockGroup = this.inventoryFormArray.at(stockIndex) as FormGroup;
    const batches = stockGroup.get('batches') as FormArray;
    const batchGroup = batches.at(batchIndex) as FormGroup;
    const batchId = batchGroup.get('id')?.value;

    if (batchId) {
      // Chama a API para excluir o lote do backend
      this.productStockService.deleteBatch(batchId).subscribe({
        next: () => {
          // Remove visualmente após sucesso
          this.removeBatchDataBase = true;
          batches.removeAt(batchIndex);
          this._alertService.showSuccess(
            'Remoção de lote',
            'O lote foi removido com sucesso.'
          );
        },
        error: err => {
          this.removeBatchDataBase = false;
          console.error('Erro ao excluir lote do backend:', err);
          this._alertService.showError(
            'Erro ao excluir lote',
            err.message || err
          );
          // Você pode exibir uma notificação de erro aqui, se quiser
        }
      });
    } else {
      // Se não tiver ID (ainda não salvo), remove só visualmente
      batches.removeAt(batchIndex);
    }
  }

  save(): void {
    if (this.stockForm.invalid) return;
    const rawValue = this.stockForm.getRawValue();

    const stocksPayload = rawValue.inventory.map((prod: any) => {
      const stockEntry: any = {
        farm_id: rawValue.farm_id,
        stock_entry_blocked: rawValue.stock_entry_blocked,
        product_id: prod.product,
        batch: prod.batches,
      };
      if (prod.minimun_level !== null && prod.minimun_level !== '') {
        stockEntry.minimun_level = prod.minimun_level;
      }
      return stockEntry;
    });

    const payload = { stocks: stocksPayload };

    this.productStockService.createStock(payload).subscribe({
      next: res => {
        if (this.data && this.data.stockData) {
          this._alertService.showSuccess(
            'Estoque atualizado com sucesso',
            'O estoque foi atualizado com sucesso.'
          );
        } else {
          this._alertService.showSuccess(
            'Novo Estoque',
            'Estoque criado com sucesso!'
          );
        }
        this.dialogRef.close(
        {
          batchDataBaseDelete: this.removeBatchDataBase,
          data: res,
        });
        
      },
      error: err => {
        if (this.data && this.data.stockData) {
          this._alertService.showError(
            'Erro ao atualizar estoque',
            err.message || err
          );
        } else {
          this._alertService.showError(
            'Erro ao criar estoque',
            err.message || err
          );
        }
        console.error('Erro ao enviar o estoque:', err);
      },
    });
  }

  cancel(): void {
    if(this.removeBatchDataBase){
      this._alertService.showSuccess(
            'Lotes removidos',
            'Ouveram exclusões de lotes do estoque.'
          );
    }
    
    this.dialogRef.close(
      {
        batchDataBaseDelete: this.removeBatchDataBase
      });
  }
}
